import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, useTheme, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../config/axiosConfig';

const IndexCardComponent = ({ symbol, label, isMobile }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [indexData, setIndexData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const isDark = theme.palette.mode === 'dark';

    useEffect(() => {
        const fetchIndexData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/api/indexfullquote/${symbol}`);
                if (response.data && response.data.data && response.data.data[0]) {
                    setIndexData(response.data.data[0]);
                }
            } catch (err) {
                console.error('Error fetching index data:', err);
                setError('Failed to load');
            } finally {
                setLoading(false);
            }
        };

        if (symbol) fetchIndexData();
    }, [symbol]);

    const handleClick = () => {
        navigate(`/asset/${encodeURIComponent(symbol)}`);
    };

    const formatPrice = (number) => {
        if (typeof number !== 'number') return '—';
        return number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatChange = (number) => {
        if (typeof number !== 'number') return '—';
        const sign = number >= 0 ? '+' : '';
        return `${sign}${number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const cardBase = {
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.18s ease',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)'}`,
        bgcolor: isDark ? 'rgba(19,47,76,0.4)' : 'background.paper',
        '&:hover': {
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)'}`,
            transform: 'translateY(-1px)',
            boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.1)',
        },
    };

    if (loading) {
        return (
            <Paper sx={{ ...cardBase, p: isMobile ? 1 : 1.5 }} elevation={0}>
                <Skeleton variant="text" width="60%" height={isMobile ? 14 : 18} />
                <Skeleton variant="text" width="80%" height={isMobile ? 22 : 32} sx={{ my: 0.5 }} />
                <Skeleton variant="text" width="40%" height={isMobile ? 14 : 16} />
                {!isMobile && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Skeleton variant="text" width="45%" height={14} />
                        <Skeleton variant="text" width="45%" height={14} />
                    </Box>
                )}
            </Paper>
        );
    }

    if (error || !indexData || typeof indexData.price === 'undefined') {
        return (
            <Paper sx={{ ...cardBase, p: isMobile ? 1 : 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: isMobile ? 70 : 100 }} elevation={0}>
                <Typography variant="caption" color="error" sx={{ opacity: 0.7 }}>Unavailable</Typography>
            </Paper>
        );
    }

    const isPositive = (indexData.changesPercentage || 0) >= 0;
    const changeColor = isPositive ? theme.palette.positive.main : theme.palette.negative.main;
    const changeBg = isPositive
        ? isDark ? 'rgba(14,179,91,0.12)' : 'rgba(14,179,91,0.1)'
        : isDark ? 'rgba(230,35,51,0.12)' : 'rgba(230,35,51,0.1)';

    // Shorten name for display
    const displayName = indexData.name || symbol;
    const shortName = displayName.length > 20 ? displayName.substring(0, 20) + '…' : displayName;

    // Mobile card
    if (isMobile) {
        return (
            <Paper sx={{ ...cardBase, p: 1, minHeight: 80 }} elevation={0} onClick={handleClick}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? 'rgba(255,255,255,0.75)' : 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '65%' }}>
                        {label || displayName.split(' ').slice(0, 2).join(' ')}
                    </Typography>
                    <Box sx={{ bgcolor: changeBg, borderRadius: 0.75, px: 0.5, py: 0.15 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: changeColor, fontSize: '0.65rem' }}>
                            {formatChange(indexData.changesPercentage)}%
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                        {formatPrice(indexData.price)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: changeColor, fontWeight: 600 }}>
                        {formatChange(indexData.change)}
                    </Typography>
                </Box>
            </Paper>
        );
    }

    // Desktop card
    return (
        <Paper sx={{ ...cardBase, p: 1.75, height: '100%' }} elevation={0} onClick={handleClick}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}>
                    {label || shortName}
                </Typography>
                <Box sx={{ bgcolor: changeBg, borderRadius: 1, px: 0.75, py: 0.25, flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: changeColor }}>
                        {formatChange(indexData.changesPercentage)}%
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1.5, mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '1.15rem' }}>
                    {formatPrice(indexData.price)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: changeColor, fontSize: '0.8rem' }}>
                    {formatChange(indexData.change)}
                </Typography>
            </Box>

            <Box sx={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`, pt: 1, display: 'flex', justifyContent: 'center', gap: 4 }}>
                <Box>
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'text.disabled', display: 'block', mb: 0.25 }}>High</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{formatPrice(indexData.dayHigh)}</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'text.disabled', display: 'block', mb: 0.25 }}>Low</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{formatPrice(indexData.dayLow)}</Typography>
                </Box>
            </Box>
        </Paper>
    );
};

export default IndexCardComponent;